/*
  *Common Imports

*/

import { PinAnchor } from "../../../../../lib/listings/pinTitle";
import { styled } from "@mui/system";
import React from "react";
/*

*Redux Imports

*/

import { setCreateNewPropertyDefaultLocationThunk } from "../../../../../redux-magic/store";

/*

* Material UI Imports

*/

import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import { Autocomplete, CircularProgress, TextField, Typography } from "@mui/material";

/*

* Google Maps Imports

*/
import { useLoadScript } from "@react-google-maps/api";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";

/*

* Icons Imports

*/

import { useRouter } from "next/router";

interface Suggestion {
	description: string;
	matched_substrings: Array<{ length: number; offset: number }>;
	place_id: string;
	reference: string;
	structured_formatting: {
		main_text: string;
		main_text_matched_substrings: Array<{ length: number; offset: number }>;
		secondary_text: string;
	};
	terms: Array<{ offset: number; value: string }>;
	types: Array<string>;
}

interface GeolocationCoordinates {
	latitude: number;
	longitude: number;
	altitude: number | null;
	accuracy: number;
	altitudeAccuracy: number | null;
	heading: number | null;
	speed: number | null;
}
interface GeolocationPosition {
	coords: GeolocationCoordinates;
}
interface GMRGCPlusCode {
	compound_code: string;
	global_code: string;
}
interface GMRGCResultsAddressComponent {
	long_name: string;
	short_name: string;
	types: Array<string>;
}
interface GMRGCResultsGeometryLocation {
	lat: number;
	lng: number;
}
interface GMRGCResultsGeometryBoundsAndGeometryViewport {
	northeast: GMRGCResultsGeometryLocation;
	southwest: GMRGCResultsGeometryLocation;
}
interface GMRGCResultsGeometry {
	bounds: GMRGCResultsGeometryBoundsAndGeometryViewport;
	location: GMRGCResultsGeometryLocation;
	location_type: string;
}
interface GMRGCResults {
	address_components: Array<GMRGCResultsAddressComponent>;
	formatted_address: string;
	geometry: GMRGCResultsGeometry;
	viewport: GMRGCResultsGeometryBoundsAndGeometryViewport;
	place_id: string;
	types: Array<string>;
}
interface GoogleMapsReverseGeocodingApiResponse {
	plus_code: GMRGCPlusCode;
	results: Array<GMRGCResults>;
	status: string;
}

const SearchContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.688rem",
	margin: "0rem 0rem 0rem 0rem",
}));

const OptionContainer = styled("li")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "flex-start",
	width: "100%",
	padding: "0.25rem 0.5rem",
	zIndex: 3,
}));

const OptionText = styled(Typography, {
	shouldForwardProp: (prop) => prop !== "isPrimary",
})<any>(({ theme, isPrimary }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	fontSize: "0.875rem",
	lineHeight: "1.3125rem",
	fontWeight: 400,
	color: isPrimary ? theme.palette.primary.main : theme.palette.text.primary,
})) as any;

const OptionSubtext = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	fontSize: "0.875rem",
	lineHeight: "1.3125rem",
	fontWeight: 400,
	color: theme.palette.text.secondary,
})) as typeof Typography;

const librariesToLoad: ("places" | "drawing" | "geometry" | "localContext" | "visualization")[] = [
	"places",
	"drawing",
	"geometry",
];

const Search = ({
	change,
	fromList,
	setFromList,
	dispatch,
	onPlacePicked,
}: {
	change: any;
	fromList: any;
	setFromList: any;
	dispatch: Function;
	/* A place was chosen (a suggestion or the current location): its spot and title anchor the
	   listing's title when the pin is moved later (lib/listings/pinTitle.ts, 3 Nov 2026). */
	onPlacePicked?: (place: PinAnchor) => void;
}) => {
	const router = React.useRef(useRouter()).current;

	/*

  & let's declare the states for the usePlacesAutocomplete hook

  */
	const {
		value,
		suggestions: { data },
		setValue,
		init,
	} = usePlacesAutocomplete({
		initOnMount: false,
		debounce: 500,
	});

	/*

  & let's declare the states for the useLoadScript hook

  */
	const { isLoaded } = useLoadScript({
		googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY_NO_REFERRER_RESTRICTIONS || "",
		libraries: librariesToLoad as any,
	});

	const [options, setOptions] = React.useState<string[]>([]);

	const [onSelectLoading, setOnSelectLoading] = React.useState(false);

	const getCurrentLocation = () => {
		setOnSelectLoading(true);
		setValue("");
		/*
    & Next, let's check if the user has allowed us to use their location and update the corresponding state accordingly.
    */
		if (!navigator.geolocation) {
			return;
		} else {
			/*
      & Next, since it looks like the user has allowed us to use their location, let's get their current location. Let's begin by disabling the location autocomplete input.
      */
			// setAllowUserInputInLocationAutocomplete(true);
			navigator.geolocation.getCurrentPosition(
				async (position: GeolocationPosition) => {
					/*
          & Next, let's extract the latitude and longitude from the position object.
          */
					const { latitude, longitude }: { latitude: number; longitude: number } = position.coords;
					/*
          & Next, let's use Google Maps' Reverse Geocoding API to get the user's current address.
          */
					const googleMapsReverseGeocodingApiResponse: GoogleMapsReverseGeocodingApiResponse = await (
						await fetch(
							"https://maps.googleapis.com/maps/api/geocode/json?latlng=" +
								latitude +
								"," +
								longitude +
								"&key=" +
								(process.env.GOOGLE_MAPS_API_KEY_NO_REFERRER_RESTRICTIONS || ""),
							{ cache: "no-store" },
						)
					).json();
					/*
          & Next, let's extract the user's current address from the API response object.
          */
					const userCurrentAddress: string | undefined =
						googleMapsReverseGeocodingApiResponse?.results[0]?.address_components?.find(
							(address_component: GMRGCResultsAddressComponent) =>
								address_component.types.includes("sublocality_level_1"),
						)?.long_name;
					const userCurrentAreas: Array<string> | undefined = googleMapsReverseGeocodingApiResponse?.results.map(
						(result) => {
							return result.address_components?.find(
								(address_component: GMRGCResultsAddressComponent) =>
									address_component.types.includes("sublocality_level_1") ||
									address_component.types.includes("postal_town") ||
									address_component.types.includes("locality"),
							)?.long_name as string;
						},
					);

					let userCurrentArea: string | undefined;

					let resultPos: number = 0;

					for (let i = 0; i < userCurrentAreas.length; i++) {
						if (userCurrentAreas[i] !== "Bengaluru") {
							userCurrentArea = userCurrentAreas[i];
							resultPos = i;
							break;
						}
					}

					/*
          & Next, let's update the corresponding state with the user's current address. Let's also set the userLocationAllowed state to true and the allowUserInputInLocationAutocomplete state to false.
          */
					const label = userCurrentAddress ? userCurrentAddress : userCurrentArea ? userCurrentArea : "";
					setValue(label);

					/*
					& let's extract country, state, and city from the same reverse-geocoding response.
					*/
					const allAddressComponents: Array<GMRGCResultsAddressComponent> =
						googleMapsReverseGeocodingApiResponse?.results?.flatMap(
							(result) => result.address_components || [],
						) || [];
					const countryName: string =
						allAddressComponents.find((component: GMRGCResultsAddressComponent) =>
							component.types.includes("country"),
						)?.long_name || "";
					const stateName: string =
						allAddressComponents.find((component: GMRGCResultsAddressComponent) =>
							component.types.includes("administrative_area_level_1"),
						)?.long_name || "";
					//? city: prefer locality, fall back to administrative_area_level_3, then level_2
					const cityName: string =
						allAddressComponents.find((component: GMRGCResultsAddressComponent) =>
							component.types.includes("locality"),
						)?.long_name ||
						allAddressComponents.find((component: GMRGCResultsAddressComponent) =>
							component.types.includes("administrative_area_level_3"),
						)?.long_name ||
						allAddressComponents.find((component: GMRGCResultsAddressComponent) =>
							component.types.includes("administrative_area_level_2"),
						)?.long_name ||
						"";

					dispatch(
						setCreateNewPropertyDefaultLocationThunk({
							location_coordinates: [latitude, longitude],
							address: userCurrentArea as string,
							country: countryName,
							state_or_province: stateName,
							city: cityName,
						}),
					);
					onPlacePicked?.({ latitude, longitude, title: (userCurrentArea as string) || "" });
					setOnSelectLoading(false);
				},
				async (error) => {},
				{ timeout: 10000, enableHighAccuracy: true },
			);
		}
		// setOnSelectLoading(false);
	};

	/*

  & let's define the function to handle the change in the location autocomplete

  */
	const onChangeLocationAutocomplete = async (event: React.SyntheticEvent<HTMLSelectElement>, tvalue: any) => {
		setValue("");
		if (tvalue === "Use current location") {
			getCurrentLocation();
		} else {
			const item = data.find((res) => res.place_id === tvalue);
			if (item) {
				const temp = item.structured_formatting.main_text;

				if (Number.isNaN(+temp)) {
					setFromList(temp);
					setValue(temp);
				} else setFromList(null);
			}
			const geocodeResult = (await getGeocode({ placeId: tvalue }))[0];
			const { lat, lng } = getLatLng(geocodeResult);
			setOnSelectLoading(true);

			/*
			& let's extract country, state, and city from the geocode result.
			*/
			const geocodeAddressComponents: Array<GMRGCResultsAddressComponent> =
				(geocodeResult?.address_components as Array<GMRGCResultsAddressComponent>) || [];
			const countryName: string =
				geocodeAddressComponents.find((component: GMRGCResultsAddressComponent) =>
					component.types.includes("country"),
				)?.long_name || "";
			const stateName: string =
				geocodeAddressComponents.find((component: GMRGCResultsAddressComponent) =>
					component.types.includes("administrative_area_level_1"),
				)?.long_name || "";
			//? city: prefer locality, fall back to administrative_area_level_3, then level_2
			const cityName: string =
				geocodeAddressComponents.find((component: GMRGCResultsAddressComponent) =>
					component.types.includes("locality"),
				)?.long_name ||
				geocodeAddressComponents.find((component: GMRGCResultsAddressComponent) =>
					component.types.includes("administrative_area_level_3"),
				)?.long_name ||
				geocodeAddressComponents.find((component: GMRGCResultsAddressComponent) =>
					component.types.includes("administrative_area_level_2"),
				)?.long_name ||
				"";

			/*
			& The title is the suggestion the user tapped. One that is only a building number
			& ("684") reads as nothing, so it falls back to the sublocality, then the locality.
			& The same rule as the apps' `bbm-android/places/resolve-place` (3 Nov 2026).
			*/
			const pickedText: string = (item?.structured_formatting.main_text as string) || "";
			const pickedTitle: string = /^[\d\s\-/,.]+$/.test(pickedText.trim())
				? ["sublocality_level_1", "sublocality_level_2", "sublocality_level_3", "sublocality"]
						.map(
							(type: string) =>
								geocodeAddressComponents.find((component: GMRGCResultsAddressComponent) =>
									component.types.includes(type),
								)?.long_name,
						)
						.find((name: string | undefined) => !!name) ||
					geocodeAddressComponents.find((component: GMRGCResultsAddressComponent) =>
						component.types.includes("locality"),
					)?.long_name ||
					cityName ||
					pickedText
				: pickedText;

			onPlacePicked?.({ latitude: lat, longitude: lng, title: pickedTitle });
			dispatch(
				setCreateNewPropertyDefaultLocationThunk({
					location_coordinates: [lat, lng],
					address: pickedTitle,
					country: countryName,
					state_or_province: stateName,
					city: cityName,
				}),
			);
			setOnSelectLoading(false);
		}
	};
	/*

  & let's use the useEffect hook to set the value of the location autocomplete to the default location and initialize the autocomplete

  */

	React.useEffect(() => {
		if (isLoaded) {
			init();
		}
	}, [isLoaded, init]);

	React.useEffect(() => {
		const mapped = data
			.map((suggestion: Suggestion) => suggestion?.place_id)
			.filter((option: string, index: number, self: string[]) => option !== "" && self.indexOf(option) === index);
		setOptions(mapped);
	}, [data]);

	const localLang = {
		useCurrentLocation:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಪ್ರಸ್ತುತ ಸ್ಥಳವನ್ನು ಬಳಸಿ." : "Use current location",
		useGPSorIP:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಜಿಪಿಎಸ್ ಅಥವಾ ಐಪಿ ವಿಳಾಸವನ್ನು ಬಳಸಿ"
					: "Use GPS or IP address to locate me",
		inTheMiddleOfNowhere:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಯಾವುದೇ ಸ್ಥಳದಲ್ಲಿ ಇಲ್ಲ" : "in the middle of nowhere",
		enterLocation: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಸ್ಥಳವನ್ನು ನಮೂದಿಸಿ" : "Enter Location",
	};

	return (
		<SearchContainer>
			<Autocomplete
				disablePortal
				slotProps={{
					clearIndicator: value ? {} : { style: { display: "none" } },
				}}
				freeSolo={true}
				id="upload-property-location-autocomplete"
				filterOptions={() => options}
				options={options}
				isOptionEqualToValue={(option: string, value: string) => {
					const item = data.find((res: any) => res.structured_formatting.main_text === value)?.place_id;
					return !item ? false : option === item;
				}}
				value={change}
				disabled={onSelectLoading}
				renderOption={(props, option) => {
					const las: any = data.find((suggestion: any) => suggestion.place_id === option);
					const optionText: string | undefined =
						option === localLang.useCurrentLocation
							? localLang.useCurrentLocation
							: las?.structured_formatting?.main_text;
					const optionSubtext: string | undefined =
						option === localLang.useCurrentLocation
							? localLang.useCurrentLocation
							: las
								? las?.structured_formatting?.secondary_text
								: undefined;

					// Destructure key from props
					const { key, ...restProps } = props;

					return (
						<OptionContainer
							key={key}
							{...restProps}
						>
							{onSelectLoading && value === "" ? (
								<CircularProgress size={19} />
							) : (
								<>
									<OptionText
										variant="body1"
										component="span"
										isPrimary={option === localLang.useCurrentLocation}
									>
										{optionText}
									</OptionText>
									<OptionSubtext
										variant="body2"
										component="span"
									>
										{optionSubtext
											? optionSubtext === localLang.useCurrentLocation
												? localLang.useGPSorIP
												: optionSubtext
											: localLang.inTheMiddleOfNowhere}
									</OptionSubtext>
								</>
							)}
						</OptionContainer>
					);
				}}
				onChange={onChangeLocationAutocomplete}
				sx={{
					width: "100%",
					borderRadius: "0.5rem",
				}}
				renderInput={(params) => (
					<TextField
						variant="outlined"
						{...params}
						size="small"
						onChange={(e) => {
							setValue(e.target.value);
						}}
						onClick={(e) => {
							/*

								* if value is not empty and user clicks on the input field, then set the value and show the suggestions

							*/
							if (value !== "") {
								setValue(value);
							}
						}}
						placeholder={onSelectLoading ? "Fetching location..." : localLang.enterLocation}
						// onKeyDown={handleKeyDown}
					/>
				)}
			/>
			{onSelectLoading ? (
				<CircularProgress size={20} />
			) : (
				<div
					style={{
						display: "flex",
						padding: "0.25rem 0rem",
						gap: "0.5rem",
						justifyContent: "space-between",
						cursor: "pointer",
					}}
					onClick={() => {
						getCurrentLocation();
					}}
				>
					{onSelectLoading && value === "" ? (
						<CircularProgress size={19} />
					) : (
						<NearMeOutlinedIcon
							sx={{
								color: "#FC8019",
								fontSize: "1.25rem",
							}}
						/>
					)}
					<Typography
						variant="subtitle2"
						sx={{
							color: "#FC8019",
						}}
					>
						{localLang.useCurrentLocation}
					</Typography>
				</div>
			)}
		</SearchContainer>
	);
};

export default Search;
